import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import TimeInput, { to24Hour } from './TimeInput';
import PlacePicker from './PlacePicker';
import useFormPersist from '../hooks/useFormPersist';
import useLocale from '../hooks/useLocale';

const Form = styled.form``;
const Grid = styled.div`
  display:grid;grid-template-columns:1fr 1fr;gap:${p => p.theme.spacing[4]}px;
  @media(max-width:${p => p.theme.breakpoints.desktop}px){grid-template-columns:1fr;}
`;
const Field = styled.div`display:flex;flex-direction:column;gap:6px;`;
const Label = styled.label`
  font-size:${p => p.theme.typography.sizes.sm};
  font-weight:${p => p.theme.typography.weights.semibold};
  color:${p => p.theme.colors.text.primary};
`;
const Input = styled.input`
  padding:10px 12px;
  border:1px solid ${p => p.theme.colors.border};
  border-radius:${p => p.theme.radius.md}px;
  font-size:${p => p.theme.typography.sizes.md};
  &:focus{border-color:${p => p.theme.colors.primary};outline:none;box-shadow:0 0 0 2px rgba(108,99,255,0.15);}
`;
const Select = styled.select`
  padding:10px 12px;
  border:1px solid ${p => p.theme.colors.border};
  border-radius:${p => p.theme.radius.md}px;
  font-size:${p => p.theme.typography.sizes.md};
  background:${p => p.theme.colors.surface};
  &:focus{border-color:${p => p.theme.colors.primary};outline:none;box-shadow:0 0 0 2px rgba(108,99,255,0.15);}
`;
const SubmitButton = styled.button`
  width:100%;padding:12px;margin-top:${p => p.theme.spacing[4]}px;
  border:none;border-radius:${p => p.theme.radius.md}px;
  background:${p => p.theme.colors.primary};color:${p => p.theme.colors.surface};
  font-size:${p => p.theme.typography.sizes.md};
  font-weight:${p => p.theme.typography.weights.semibold};
  cursor:pointer;
  &:hover{background:#5a52d5;}
  &:disabled{opacity:0.6;cursor:not-allowed;}
`;
const ErrorBox = styled.div`
  background:${p => p.theme.colors.error.bg};
  color:${p => p.theme.colors.error.text};
  border-radius:${p => p.theme.radius.md}px;
  padding:${p => p.theme.spacing[3]}px ${p => p.theme.spacing[4]}px;
  margin-top:${p => p.theme.spacing[3]}px;
`;
// PrefillBanner background intentionally hardcoded (#f3f0ff) — page-specific UX color, not a semantic token
const Banner = styled.div`
  background:#f3f0ff;border-radius:${p => p.theme.radius.md}px;
  padding:${p => p.theme.spacing[3]}px ${p => p.theme.spacing[4]}px;
  margin-bottom:${p => p.theme.spacing[4]}px;
  display:flex;align-items:center;gap:${p => p.theme.spacing[3]}px;
  font-size:${p => p.theme.typography.sizes.sm};
`;
const BannerBtn = styled.button`
  border:none;padding:6px 12px;border-radius:${p => p.theme.radius.sm}px;cursor:pointer;
  font-size:${p => p.theme.typography.sizes.xs};
  &:first-of-type{background:${p => p.theme.colors.primary};color:${p => p.theme.colors.surface};}
  &:last-of-type{background:transparent;color:${p => p.theme.colors.text.secondary};text-decoration:underline;}
`;

const ALL_FIELDS = ['name', 'gender', 'dob', 'time', 'place', 'ayanamsa'];

export default function BirthDataForm({
  fields = ALL_FIELDS, values, onChange, onSubmit,
  submitLabel, loading = false, error = null,
  initialValues, autoSubmit = false,
  ayanamsaOptions, pageName, lang = 'en',
  showSubmit = true,
}) {
  const t = useLocale(lang, 'vedic');
  const ft = t.forms || {};
  const autoPopulateRef = useRef(false);
  const [showBanner, setShowBanner] = useState(false);

  useFormPersist(pageName, values, onChange);

  // Cross-page pre-fill banner
  useEffect(() => {
    if (values && values.dob) return;
    try {
      const lastData = sessionStorage.getItem('vedic_last_birthdata');
      if (lastData) setShowBanner(true);
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // autoPopulate
  useEffect(() => {
    if (!initialValues || autoPopulateRef.current) return;
    if (Object.keys(initialValues).length === 0) return;
    autoPopulateRef.current = true;
    onChange(prev => ({ ...prev, ...initialValues }));
    if (autoSubmit) {
      setTimeout(() => onSubmit({ ...values, ...initialValues }), 300);
    }
  }, [initialValues, autoSubmit]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUseBanner = () => {
    try {
      const data = JSON.parse(sessionStorage.getItem('vedic_last_birthdata'));
      if (data) {
        // Drop undefined values so we don't clobber existing form defaults
        // (e.g., another page may have saved this without `gender`).
        const clean = Object.fromEntries(
          Object.entries(data).filter(([, v]) => v !== undefined && v !== null)
        );
        onChange(prev => ({ ...prev, ...clean }));
      }
    } catch {}
    setShowBanner(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!onSubmit) return;
    const h24 = to24Hour(values.hour, values.period, values.timeFormat);
    const time = `${String(h24).padStart(2, '0')}:${String(values.minute || 0).padStart(2, '0')}`;
    onSubmit({ ...values, time });
  };

  const hasField = useCallback((f) => fields.includes(f), [fields]);

  const ayanamsaOpts = (ayanamsaOptions || []).filter(Boolean);

  return (
    <Form onSubmit={handleSubmit}>
      {showBanner && (
        <Banner>
          <span>Birth data from your last session is available.</span>
          <BannerBtn type="button" onClick={handleUseBanner}>Use it</BannerBtn>
          <BannerBtn type="button" onClick={() => setShowBanner(false)}>Dismiss</BannerBtn>
        </Banner>
      )}
      <Grid>
        {hasField('name') && (
          <Field>
            <Label>{ft.name || 'Full Name'}</Label>
            <Input type="text" value={values.name || ''} onChange={e => onChange(p => ({ ...p, name: e.target.value }))} disabled={loading} />
          </Field>
        )}
        {hasField('gender') && (
          <Field>
            <Label>{ft.gender || 'Gender'}</Label>
            <Select value={values.gender} onChange={e => onChange(p => ({ ...p, gender: e.target.value }))} disabled={loading}>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </Select>
          </Field>
        )}
        {hasField('dob') && (
          <Field>
            <Label>{ft.dob || 'Date of Birth'}</Label>
            <Input type="date" value={values.dob || ''} onChange={e => onChange(p => ({ ...p, dob: e.target.value }))} disabled={loading} />
          </Field>
        )}
        {hasField('time') && (
          <TimeInput
            hour={values.hour} minute={values.minute} period={values.period}
            timeFormat={values.timeFormat}
            onHourChange={v => onChange(p => ({ ...p, hour: v }))}
            onMinuteChange={v => onChange(p => ({ ...p, minute: v }))}
            onPeriodChange={v => onChange(p => ({ ...p, period: v }))}
            onFormatChange={v => onChange(p => ({ ...p, timeFormat: v }))}
            disabled={loading}
          />
        )}
        {hasField('place') && (
          <PlacePicker
            value={values.place} lat={values.lat || values.latitude} lon={values.lon || values.longitude} timezone={values.timezone}
            onChange={loc => onChange(p => ({ ...p, ...loc, latitude: loc.lat, longitude: loc.lon }))}
            label={ft.place || 'Place of Birth'}
            disabled={loading}
          />
        )}
        {hasField('ayanamsa') && ayanamsaOpts.length > 0 && (
          <Field>
            <Label>{ft.ayanamsa || 'Ayanamsa'}</Label>
            <Select value={values.ayanamsa || ''} onChange={e => onChange(p => ({ ...p, ayanamsa: e.target.value }))} disabled={loading}>
              {ayanamsaOpts.map(a => {
                const mode = typeof a === 'string' ? a : a.mode;
                const name = typeof a === 'string' ? a : a.display_name;
                return <option key={mode} value={mode}>{name}</option>;
              })}
            </Select>
          </Field>
        )}
      </Grid>
      {showSubmit && (
        <SubmitButton type="submit" disabled={loading}>
          {loading ? (ft.loading || 'Computing...') : (submitLabel || ft.compute || 'Compute')}
        </SubmitButton>
      )}
      {error && <ErrorBox>{error}</ErrorBox>}
    </Form>
  );
}
